import { useRef, useState } from "react";
import { toast } from "sonner";
import { Trophy, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/context/LanguageContext";
import { ShareChampionButton } from "@/components/ShareCard";

const monthName = (m) => new Date(`${m}-01`).toLocaleDateString("en-IN", { month: "long", year: "numeric" });

const ChampionDoc = ({ c, innerRef }) => (
  <div ref={innerRef} style={{ width: 1123, height: 794, background: "#0A192F", fontFamily: "'Outfit','Noto Sans Devanagari',sans-serif", padding: 28, boxSizing: "border-box" }}>
    <div style={{ border: "3px solid #FF2E63", height: "100%", boxSizing: "border-box", padding: "44px 70px", textAlign: "center", color: "#fff", position: "relative",
      background: "radial-gradient(700px 350px at 50% 0%, rgba(255,46,99,0.25), transparent), radial-gradient(500px 300px at 100% 100%, rgba(0,229,255,0.15), transparent)" }}>
      <div style={{ fontSize: 24, fontWeight: 800 }}><span style={{ color: "#FF2E63" }}>Robo</span>Spark <span style={{ fontSize: 11, letterSpacing: 3, opacity: 0.6 }}>PRP PVT LTD</span></div>
      <div style={{ marginTop: 30, fontSize: 16, letterSpacing: 6, color: "#00E5FF", fontWeight: 700 }}>CERTIFICATE OF EXCELLENCE · उत्कृष्टता प्रमाणपत्र</div>
      <div style={{ marginTop: 10, fontSize: 60, fontWeight: 900, color: "#ffb800", letterSpacing: 2 }}>MAKER OF THE MONTH</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: "#FF2E63" }}>मेकर ऑफ़ द मंथ · {monthName(c.month)}</div>
      <div style={{ marginTop: 30, fontSize: 15, opacity: 0.75 }}>Awarded to · प्रदान किया गया</div>
      <div style={{ marginTop: 6, fontSize: 50, fontWeight: 800, borderBottom: "2px solid #FF2E63", display: "inline-block", padding: "0 30px 6px" }}>{c.student_name}</div>
      <div style={{ marginTop: 18, fontSize: 16, opacity: 0.85, lineHeight: 1.7 }}>
        Class {c.class_grade}, {c.school_name} — for being the top builder across all RoboSpark partner schools<br />
        with <b style={{ color: "#ffb800" }}>{c.projects} completed project{c.projects === 1 ? "" : "s"}</b> in the {c.program} program.
      </div>
      <div style={{ position: "absolute", left: 70, right: 70, bottom: 40, display: "flex", justifyContent: "space-between", alignItems: "flex-end", fontSize: 13 }}>
        <div style={{ textAlign: "left", opacity: 0.85 }}>Certificate No: <b>{c.champion_no}</b><br /><span style={{ fontSize: 11 }}>Verify: robospark.prp@gmail.com</span></div>
        <div style={{ width: 100, height: 100, borderRadius: "50%", background: "#ffb800", color: "#0A192F", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: 14, lineHeight: 1.15, boxShadow: "0 0 0 6px #0A192F, 0 0 0 8px #ffb800" }}>No.1<br />MAKER</div>
        <div style={{ textAlign: "right" }}><div style={{ borderTop: "1px solid #fff", paddingTop: 4, width: 220 }}>Director, RoboSpark PrP Pvt Ltd</div><span style={{ fontSize: 11, opacity: 0.7 }}>C-30, C-Block, Sector 63, Noida-201301 (U.P.)</span></div>
      </div>
    </div>
  </div>
);

export const ChampionCertificates = ({ champions, photo, schoolLabel = "Partner School" }) => {
  const { L } = useLang();
  const refs = useRef({});
  const [busy, setBusy] = useState(null);
  if (!champions?.length) return null;

  const download = async (c) => {
    setBusy(c.month);
    try {
      const html2pdf = (await import("html2pdf.js")).default;
      await html2pdf().set({
        margin: 0, filename: `RoboSpark-Maker-of-the-Month-${c.month}-${c.student_name.replace(/\s+/g, "-")}.pdf`,
        image: { type: "jpeg", quality: 0.95 }, html2canvas: { scale: 2 },
        jsPDF: { unit: "px", format: [1123, 794], orientation: "landscape", hotfixes: ["px_scaling"] },
      }).from(refs.current[c.month]).save();
    } catch { toast.error(L("Could not create certificate.", "प्रमाणपत्र नहीं बन सका।")); } finally { setBusy(null); }
  };

  return (
    <div className="space-y-3" data-testid="champion-certificates">
      {champions.map((c) => (
        <div key={c.month} className="rounded-2xl p-5 bg-gradient-to-r from-[#FF2E63] to-[#ffb800] text-white flex flex-wrap items-center justify-between gap-4" data-testid={`champion-card-${c.month}`}>
          <div className="flex items-center gap-3">
            <Trophy className="w-8 h-8" />
            <div><div className="font-extrabold text-lg">{L("Maker of the Month", "मेकर ऑफ़ द मंथ")} · {monthName(c.month)}</div><div className="text-sm text-white/85">{c.projects} {L("projects", "प्रोजेक्ट")} · {c.champion_no}</div></div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <ShareChampionButton testid={`champion-share-${c.month}`}
              data={{ first_name: (c.name || "").split(" ")[0], school_name: schoolLabel, month: c.month, program: c.program, photo }} />
            <Button onClick={() => download(c)} disabled={!!busy} className="rounded-full bg-[#0A192F] hover:bg-black text-white" data-testid={`champion-download-${c.month}`}>
              {busy === c.month ? <Loader2 className="w-4 h-4 animate-spin" /> : L("Download certificate", "प्रमाणपत्र डाउनलोड करें")}
            </Button>
          </div>
          <div aria-hidden style={{ position: "fixed", left: -14000, top: 0 }}><ChampionDoc c={c} innerRef={(el) => { refs.current[c.month] = el; }} /></div>
        </div>
      ))}
    </div>
  );
};
